import { Address, isAddress, isAddressEqual, zeroAddress } from "viem";
import { arbitrum, sepolia } from "viem/chains";

/**
 * Every interest-rate model ever deployed per chain, labeled by generation.
 *
 * Loan terms embed the model address that was current when the loan was
 * originated, so live loans span multiple generations at once. Classifying a
 * model by comparing against a single "current" address therefore silently
 * misreads every older loan — and because the classifier's fallthrough is
 * "not amortized => simple", the failure is invisible: an amortized loan gets
 * scheduled as equal-principal instalments with no error and no revert.
 *
 * That is exactly what happened when gen 4 landed on arbitrum (2026-07-31): the
 * two loans on the new amortized model were scheduled as simple, overstating
 * their next payment by ~39k and ~50k against the on-chain `quote`.
 *
 * Source of truth: `deployments/<chain>.json` in usdai-loan-router-contracts-private,
 * reconstructed from that file's git history. When a model is redeployed, append
 * a new entry here — never edit an existing one, or loans originated against it
 * become unclassifiable.
 */

export type InterestRateModelKind = "simple" | "amortized";

export interface InterestRateModelEntry {
  kind: InterestRateModelKind;
  address: Address;
  /** 1 = oldest. The highest generation present for a chain is the current one. */
  generation: number;
  /** Date the address landed in the contracts repo's deployments file. */
  deployedAt: string;
}

export const INTEREST_RATE_MODELS: Record<number, InterestRateModelEntry[]> = {
  [arbitrum.id]: [
    { kind: "amortized", generation: 1, deployedAt: "2025-12-03", address: "0xe01520FD4F249efa4d905F60b6b54E2ade6fA18D" },
    { kind: "simple", generation: 1, deployedAt: "2025-12-03", address: "0x92E1A11D0689AdB40F0abcc0aEEF7B36fF48711a" },
    { kind: "simple", generation: 2, deployedAt: "2025-12-16", address: "0xEA0eae46bC15cd975F0545ba35584D96c7b35A1e" },
    { kind: "amortized", generation: 3, deployedAt: "2026-06-25", address: "0x23ea0944271c0A69d9816490218af1e13967bd87" },
    { kind: "simple", generation: 3, deployedAt: "2026-06-25", address: "0xF97892DCcE1318480BAF8765B4a8eA7b6048c1e1" },
    { kind: "amortized", generation: 4, deployedAt: "2026-07-31", address: "0x060CE8d04fAc0D85Baa4208331059D8CAb7A6aa1" },
    { kind: "simple", generation: 4, deployedAt: "2026-07-31", address: "0x5d57017DBB71C65Bf581FE08d5E5dc072beD8b13" },
  ],
  [sepolia.id]: [
    { kind: "amortized", generation: 1, deployedAt: "2025-11-20", address: "0x0c6A2187579680F47A014A72F9e7A202E3cEeB3e" },
    { kind: "simple", generation: 1, deployedAt: "2025-11-20", address: "0xab608ec318d2C185C17348DD5845EF9eDE30bCf1" },
    { kind: "simple", generation: 2, deployedAt: "2025-12-16", address: "0x2dBb440a0b53AB5C4144E79042FB5Bd34016a0aC" },
    { kind: "amortized", generation: 3, deployedAt: "2026-06-30", address: "0x20446F8E372F7E534e78Fa1cA45Ccfc8f15664A3" },
    { kind: "simple", generation: 3, deployedAt: "2026-06-30", address: "0x6Ad96f11cCed9AeCDD743E837b20C87CafD55D2B" },
  ],
};

/** The model matching `address` on `chain`, across every generation. */
export function classifyInterestRateModel(
  chain: number,
  address: Address | undefined | null,
): InterestRateModelEntry | undefined {
  /* `isAddress` first: viem's `isAddressEqual` THROWS on a malformed value, and this
   * runs in render paths. The field is only `Address` at compile time — the runtime
   * value comes from the DB — so a truncated string must degrade to "unclassified"
   * rather than crash the loans page. */
  if (!address || !isAddress(address) || isAddressEqual(address, zeroAddress)) return undefined;
  return INTEREST_RATE_MODELS[chain]?.find((entry) => isAddressEqual(entry.address, address));
}

/* Unrecognized models are reported once each — these run inside render/useMemo,
 * so an unguarded log would fire on every paint. */
const reported = new Set<string>();

/**
 * Whether `address` is an amortized model on `chain`.
 *
 * Unknown addresses fall back to `false` (simple), preserving the behavior this
 * replaced, but are logged: a newly deployed model missing from the registry
 * above is the one failure mode that produces wrong numbers silently.
 */
export function isAmortizedModel(chain: number, address: Address | undefined | null): boolean {
  const entry = classifyInterestRateModel(chain, address);
  if (entry) return entry.kind === "amortized";

  /* zeroAddress means "model not chosen yet" on an unfunded draft — expected, not a gap. */
  if (address && isAddress(address) && !isAddressEqual(address, zeroAddress)) {
    const key = `${chain}:${address.toLowerCase()}`;
    if (!reported.has(key)) {
      reported.add(key);
      console.error(
        "Unrecognized interest rate model — payment schedules for this loan are being " +
          "computed as SIMPLE and may be wrong. Add it to INTEREST_RATE_MODELS.",
        { chain, address },
      );
    }
  }
  return false;
}

/** The current-generation model address for `kind`. */
export function currentInterestRateModel(chain: number, kind: InterestRateModelKind): Address {
  const candidates = (INTEREST_RATE_MODELS[chain] ?? []).filter((entry) => entry.kind === kind);
  if (candidates.length === 0) return zeroAddress;
  return candidates.reduce((a, b) => (b.generation > a.generation ? b : a)).address;
}
